import { apiClient } from '@/lib/api/client';
import type { NotificationCategory } from '@/features/communications/centerTypes';

export type PreferenceChannel = 'in_app' | 'email' | 'push';

/** GET /notifications item (Phase 1 shape + Notification Center category and message id). */
export interface CenterNotification {
  id: string;
  /** Source type (domain event type, "message", ...). */
  category: string;
  /** Notification Center category (announcements, attendance, ...). */
  notificationCategory?: string | null;
  title: string;
  body: string | null;
  linkPath: string | null;
  messageId?: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationFeed {
  data: CenterNotification[];
  meta: { unreadCount: number; nextBefore?: string | null };
}

export interface UnreadCounts {
  total: number;
  byCategory: Record<string, number>;
}

export interface CategoryPreference {
  category: string;
  label: string;
  description: string;
  mandatory: boolean;
  channels: Record<PreferenceChannel, { enabled: boolean; isDefault: boolean }>;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export const notificationsApi = {
  feed: (
    query: { unreadOnly?: boolean; category?: string; before?: string; limit?: number },
    signal?: AbortSignal,
  ) =>
    apiClient
      .get<NotificationFeed>('/notifications', {
        query: {
          unreadOnly: query.unreadOnly || undefined,
          category: query.category,
          before: query.before,
          limit: query.limit,
        },
        signal,
      })
      .then((r): NotificationFeed => ({
        data: Array.isArray(r?.data) ? r.data : [],
        meta: {
          unreadCount: r?.meta?.unreadCount ?? 0,
          nextBefore: r?.meta?.nextBefore ?? null,
        },
      })),
  categories: (signal?: AbortSignal) =>
    apiClient
      .get<{ data: NotificationCategory[] }>('/notifications/categories', { signal })
      .then((r) => (Array.isArray(r?.data) ? r.data : [])),
  unreadCounts: (signal?: AbortSignal) =>
    apiClient
      .get<{ data: UnreadCounts }>('/notifications/unread-counts', { signal })
      .then((r): UnreadCounts | null =>
        isObject(r?.data) && isObject(r.data.byCategory) ? r.data : null,
      ),
  markRead: (id: string) => apiClient.post(`/notifications/${id}/read`),
  markBulk: (body: { all: true } | { category: string } | { ids: string[] }) =>
    apiClient.post<{ data: { updated: number } }>('/notifications/read', body),
  preferences: (signal?: AbortSignal) =>
    apiClient
      .get<{ data: CategoryPreference[] }>('/notifications/preferences', { signal })
      .then((r) => (Array.isArray(r?.data) ? r.data : [])),
  updatePreferences: (
    changes: { category: string; channel: PreferenceChannel; enabled: boolean }[],
  ) =>
    apiClient
      .put<{ data: CategoryPreference[] }>('/notifications/preferences', { changes })
      .then((r) => (Array.isArray(r?.data) ? r.data : [])),
};
